import config from '../config';
import { buildResponseError } from './http';

/**
 * Create a new user on the server.
 * @param {Object} userData - The user payload matching the server model.
 * @returns {Promise<Object>} The created user.
 */
export const createUser = async (userData) => {
  const response = await fetch(`${config.apiUrl}/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    throw await buildResponseError(
      response,
      response.status === 409
        ? 'User with this username, email, or mobile already exists.'
        : 'Failed to create user'
    );
  }
  return response.json();
};

/**
 * Check whether a username is available.
 * @param {string} userName
 * @returns {Promise<Object>} The availability response.
 */
export const checkUsernameAvailability = async (userName) => {
  const response = await fetch(`${config.apiUrl}/users/check-username`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_name: userName }),
  });

  if (!response.ok) {
    throw await buildResponseError(response, 'Failed to check username availability');
  }

  return response.json();
};

/**
 * Check whether a username is available.
 * Mirrors /users/check-username.
 * @param {string} userName
 * @returns {Promise<Object>} The availability response.
 */
export const checkUsername = async (userName) => {
  const response = await fetch(`${config.apiUrl}/users/check-username`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_name: userName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to check username availability');
  }

  return response.json();
};

/**
 * Lookup users by user name, full name, mobile, or email.
 * Mirrors /users/lookup.
 * @param {string|Object} query - Search term string or query object.
 * @returns {Promise<Object>} Matching users response.
 */
export const lookupUsers = async (query) => {
  const queryParams = new URLSearchParams(
    typeof query === 'string'
      ? { q: query }
      : Object.entries(query || {}).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {})
  ).toString();

  const response = await fetch(`${config.apiUrl}/users/lookup${queryParams ? `?${queryParams}` : ''}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to lookup users');
  }

  return response.json();
};

/**
 * Fetch all users.
 */
export const getAllUsers = async () => {
  const response = await fetch(`${config.apiUrl}/users`);
  if (!response.ok) throw new Error('Failed to fetch users');
  return response.json();
};

/**
 * Fetch one user by ID (fresh balance: vigor_points, vigor_coins, ...).
 * @param {number|string} id
 * @returns {Promise<Object>} The user.
 */
export const getUserById = async (id) => {
  const response = await fetch(`${config.apiUrl}/users/${id}`);
  if (!response.ok) {
    throw await buildResponseError(response, 'Failed to load user');
  }
  return response.json();
};

/**
 * Find a user by email/mobile/username.
 */
export const findUser = async (searchField) => {

  const response = await fetch(`${config.apiUrl}/users/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ searchField }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'User not found');
  }
  return response.json();
};

/**
 * Update a user by ID.
 */
export const updateUser = async (id, data) => {
  const response = await fetch(`${config.apiUrl}/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update user');
  }
  return response.json();
};

export const updateClientProfilePhoto = async (userData,user_id) => {
  const response = await fetch(`${config.apiUrl}/users/${user_id}/profile-photo`, {
    method: 'PUT',
    body: userData,
  });

  if (!response.ok) {
    const errorResponse = await response.json(); // Parse the error response
    throw new Error(JSON.stringify(errorResponse)); // Throw the real error message
  }
  return response.json();
};

/**
 * Update user card and track it.
 * @param {Object} data - { user_id, card_number, mediator_id, action }
 */
export const updateUserCard = async (data) => {
  const response = await fetch(`${config.apiUrl}/users/updatecard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update user card');
  }
  return response.json();
};

/**
 * Assign (replace) card to a user.
 * Mirrors /users/replace-card.
 * @param {Object} data
 * @returns {Promise<Object>} Replace-card response.
 */
export const replaceUserCard = async (data) => {
  const response = await fetch(`${config.apiUrl}/users/replace-card`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to replace user card');
  }

  return response.json();
};

/**
 * Quick admin user creation with backend defaults for username/password.
 * Mirrors /users/admin/quick-create.
 * @param {Object} data
 * @returns {Promise<Object>} Created admin user response.
 */
export const quickCreateAdminUser = async (data = {}) => {
  const response = await fetch(`${config.apiUrl}/users/admin/quick-create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to quick-create admin user');
  }

  return response.json();
};

/**
 * Track token recharge by a mediator.
 * @param {Object} data - { user_id, mediator_id, tokens, paid_amount }
 */
export const createRechargeTracking = async (data) => {
  const response = await fetch(`${config.apiUrl}/users/recharge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create recharge tracking');
  }
  return response.json();
};

/**
 * Filter card tracking records by query params.
 * @param {Object} params - { mid_id, isCleared, startDate, endDate }
 */
export const filterTracking = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`${config.apiUrl}/users/filterTracking/get?${query}`);
  if (!response.ok) throw new Error('Failed to fetch card tracking records');
  return response.json();
};

/**
 * Filter recharge tracking records by query params.
 * @param {Object} params - { mid_id, isCleared, startDate, endDate }
 */
export const filterRechargeTracking = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`${config.apiUrl}/users/filterRecharge/get?${query}`);
  if (!response.ok) throw new Error('Failed to fetch recharge tracking records');
  return response.json();
};

/**
 * Clear specific card tracking records by IDs.
 * @param {Array<number>} ids
 */
export const clearTrackingRecords = async (ids) => {
  const response = await fetch(`${config.apiUrl}/users/clearCards`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) throw new Error('Failed to clear card tracking records');
  return response.json();
};

/**
 * Clear specific recharge tracking records by IDs.
 * @param {Array<number>} ids
 */
export const clearRechargeTracking = async (ids) => {
  const response = await fetch(`${config.apiUrl}/users/clearRecharge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) throw new Error('Failed to clear recharge tracking records');
  return response.json();
};

/**
 * Get all played game sessions for a user.
 * @param {number|string} userId
 * @param {Object} params - Optional filters: { locationId, gameModeId }
 * @returns {Promise<Object>} User game session history response.
 */
export const getUserPlayedGameSessions = async (userId, params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ).toString();
  const url = `${config.apiUrl}/game-session/user/${userId}${query ? `?${query}` : ''}`;

  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to fetch game session history');
  }

  return response.json();
};